import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Button,
  Stack,
  Divider,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PrintOutlined as PrintIcon,
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  PlaceOutlined as GpsIcon,
  VerifiedUserOutlined as VerifyIcon,
} from '@mui/icons-material';
import logo from '../assets/images/Inspect IQ Logo.svg';
import Seo from '../components/Seo';
import { trackEvent } from '../utils/analytics';
import marineImg from '../assets/images/MarineInspection.jpeg';
import containerImg from '../assets/images/ContainerInspection.avif';
import cargoImg from '../assets/images/GeneralAndLiquidCargo.avif';
import projectImg from '../assets/images/ProjectOverDimentional.jpeg';

const meta = [
  ['Report No.', 'IQ-2026-PDL-0481'],
  ['Service Type', 'Pre-Dispatch Loading Inspection'],
  ['Client', 'Meridian Commodities Pvt. Ltd.'],
  ['Inspection Date', '04 June 2026'],
  ['Location', 'Mundra Port, Gujarat, India'],
  ['Vessel / Carrier', 'MV Northern Star'],
  ['Cargo', 'Bagged Soybean Meal'],
  ['Inspector', 'A. Sharma (Cargo Surveyor)'],
];

const checklist = [
  { item: 'Container / hold clean, dry and odour-free', result: 'pass' },
  { item: 'No visible damage or prior cargo residue', result: 'pass' },
  { item: 'Cargo marks & numbers match documents', result: 'pass' },
  { item: 'Packaging intact, no torn or wet bags', result: 'pass' },
  { item: 'Dunnage and lashing adequate', result: 'pass' },
  { item: 'Moisture levels within tolerance', result: 'fail' },
  { item: 'Seal affixed and recorded', result: 'pass' },
];

const tally = [
  ['1–250', 'Soybean Meal', '50 kg', '250', '12,500 kg'],
  ['251–500', 'Soybean Meal', '50 kg', '250', '12,500 kg'],
  ['501–750', 'Soybean Meal', '50 kg', '250', '12,500 kg'],
  ['751–1000', 'Soybean Meal', '50 kg', '250', '12,500 kg'],
];

const photos = [
  { img: containerImg, caption: 'Empty hold — pre-loading condition' },
  { img: cargoImg, caption: 'Cargo staging & bag condition' },
  { img: marineImg, caption: 'Loading supervision in progress' },
  { img: projectImg, caption: 'Stowage & lashing arrangement' },
];

const Field = ({ label, value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#7a8c95', fontWeight: 700 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '0.95rem', color: '#0f2733', fontWeight: 500 }}>{value}</Typography>
  </Box>
);

const DemoReport = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: '#e9eef1', py: { xs: 3, md: 5 } }}>
      <Seo
        title="Sample Inspection Report"
        description="See a sample of the branded inspection report InspectIQ's platform generates automatically — metadata, checklist, cargo tally, photo evidence and site verification."
        path="/platform/demo-report"
      />
      {/* Action bar (not printed) */}
      <Container maxWidth="md" className="no-print" sx={{ mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ sm: 'center' }}>
          <Box>
            <Chip label="Sample Report" color="secondary" size="small" sx={{ fontWeight: 700, mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              An example of an InspectIQ auto-generated report. Data is illustrative.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button variant="outlined" startIcon={<BackIcon />} onClick={() => navigate('/platform')}>
              Back to Platform
            </Button>
            <Button variant="contained" startIcon={<PrintIcon />} onClick={() => { trackEvent('print_sample_report'); window.print(); }}>
              Print / Save PDF
            </Button>
          </Stack>
        </Stack>
      </Container>

      {/* The "document" */}
      <Container maxWidth="md">
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(15,39,51,0.15)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ background: 'linear-gradient(135deg, #013e5c 0%, #01567a 100%)', color: 'white', p: { xs: 3, md: 4 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box component="img" src={logo} alt="InspectIQ" sx={{ height: 40, filter: 'brightness(0) invert(1)' }} />
                <Box>
                  <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 800, fontSize: '1.3rem' }}>
                    InspectIQ
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)' }}>
                    Independent Inspection & Survey
                  </Typography>
                </Box>
              </Stack>
              <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: '1.1rem' }}>
                  INSPECTION REPORT
                </Typography>
                <Chip label="APPROVED" size="small" sx={{ bgcolor: '#2e7d32', color: 'white', fontWeight: 700, mt: 0.5 }} />
              </Box>
            </Stack>
          </Box>

          {/* Body */}
          <Box sx={{ p: { xs: 3, md: 4 } }}>
            {/* Metadata */}
            <Grid container spacing={2}>
              {meta.map(([label, value]) => (
                <Grid item xs={6} sm={3} key={label}>
                  <Field label={label} value={value} />
                </Grid>
              ))}
            </Grid>

            {/* Site verification */}
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f8fa', borderRadius: 2, border: '1px dashed rgba(1,86,122,0.3)' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <VerifyIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f2733' }}>
                  Site Verification
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: '#52707d' }}>
                <GpsIcon sx={{ fontSize: 16 }} />
                <Typography sx={{ fontSize: '0.8rem' }}>
                  22.7397° N, 69.7035° E · Captured 04 Jun 2026, 09:14 IST · Geotagged selfie on file
                </Typography>
              </Stack>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Checklist */}
            <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: '1.05rem', mb: 1.5, color: '#0f2733' }}>
              Inspection Checklist
            </Typography>
            <Stack spacing={0.5}>
              {checklist.map((c, i) => (
                <Stack key={i} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1, borderBottom: '1px solid #eef2f4' }}>
                  <Typography sx={{ fontSize: '0.9rem', color: '#0f2733' }}>{c.item}</Typography>
                  {c.result === 'pass' ? (
                    <Chip icon={<PassIcon />} label="Pass" size="small" sx={{ bgcolor: 'rgba(46,125,50,0.1)', color: '#2e7d32', fontWeight: 700, '& .MuiChip-icon': { color: '#2e7d32' } }} />
                  ) : (
                    <Chip icon={<FailIcon />} label="Attention" size="small" sx={{ bgcolor: 'rgba(211,47,47,0.1)', color: '#c62828', fontWeight: 700, '& .MuiChip-icon': { color: '#c62828' } }} />
                  )}
                </Stack>
              ))}
            </Stack>

            <Divider sx={{ my: 3 }} />

            {/* Tally table */}
            <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: '1.05rem', mb: 1.5, color: '#0f2733' }}>
              Cargo Tally & Weight Log
            </Typography>
            <Box sx={{ border: '1px solid #e3e9ec', borderRadius: 1.5, overflow: 'hidden' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 1fr 1fr 1.2fr' }}>
                {['Bag Range', 'Commodity', 'Unit Wt.', 'Bags', 'Total Wt.'].map((h) => (
                  <Box key={h} sx={{ p: 1.25, bgcolor: '#013e5c', color: 'white', fontSize: '0.78rem', fontWeight: 700 }}>{h}</Box>
                ))}
                {tally.map((row, ri) => row.map((cell, ci) => (
                  <Box key={`${ri}-${ci}`} sx={{ p: 1.25, fontSize: '0.82rem', color: '#0f2733', borderTop: '1px solid #eef2f4', bgcolor: ri % 2 ? '#fafbfc' : 'white' }}>{cell}</Box>
                )))}
                <Box sx={{ p: 1.25, fontSize: '0.82rem', fontWeight: 700, borderTop: '2px solid #013e5c', gridColumn: '1 / 4' }}>Total</Box>
                <Box sx={{ p: 1.25, fontSize: '0.82rem', fontWeight: 700, borderTop: '2px solid #013e5c', color: '#0f2733' }}>1,000</Box>
                <Box sx={{ p: 1.25, fontSize: '0.82rem', fontWeight: 700, borderTop: '2px solid #013e5c', color: 'primary.main' }}>50,000 kg</Box>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Photo gallery */}
            <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: '1.05rem', mb: 1.5, color: '#0f2733' }}>
              Photographic Evidence
            </Typography>
            <Grid container spacing={2}>
              {photos.map((p, i) => (
                <Grid item xs={6} sm={3} key={i}>
                  <Box sx={{ borderRadius: 1.5, overflow: 'hidden', border: '1px solid #e3e9ec' }}>
                    <Box component="img" src={p.img} alt={p.caption} sx={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }} />
                    <Typography sx={{ fontSize: '0.7rem', color: '#52707d', p: 1 }}>{p.caption}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Remarks */}
            <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: '1.05rem', mb: 1, color: '#0f2733' }}>
              Surveyor Remarks
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', color: '#52707d', lineHeight: 1.7, mb: 3 }}>
              Cargo was found in generally sound condition and loading was supervised throughout.
              A localized batch (bags 612–640) showed moisture above the agreed tolerance; these
              were segregated and reported to the client prior to stowage. Container was sealed
              (Seal No. IQ-558217) on completion. All other parameters conform to the agreed scope.
            </Typography>

            {/* Signoff */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Field label="Inspected By" value="A. Sharma" />
              </Grid>
              <Grid item xs={6}>
                <Field label="Approved By" value="N. K. Gupta (Director)" />
              </Grid>
            </Grid>

            {/* Disclaimer */}
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #eef2f4' }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#9aabb2', lineHeight: 1.6 }}>
                Disclaimer: This report reflects findings at the time and place of inspection only and
                does not relieve any party of contractual obligations. InspectIQ acts as an independent
                third party. This is a sample document with illustrative data generated to demonstrate the
                InspectIQ platform.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Footer CTA (not printed) */}
        <Box className="no-print" sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This is exactly the kind of report InspectIQ generates automatically — in under 3 seconds.
          </Typography>
          <Button variant="contained" size="large" onClick={() => navigate('/contact')} sx={{ px: 4, py: 1.5 }}>
            Request a Demo
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default DemoReport;
